import * as React from 'react'

import { useRouter } from 'next/router'
import Head from 'next/head'
import getConfig from 'next/config'
import { GetServerSidePropsContext } from 'next'
import { LoadScript } from '@react-google-maps/api'

import { vsprintf } from 'sprintf-js'

import {
  EnterpriseNextPage,
  PageWrapper,
  useSiteConfig,
  withCacheConfiguration,
  i18n,
  useSiteDefinition,
  Routes,
} from '@thg-commerce/enterprise-core'

import { mq, styled, Text } from '@thg-commerce/enterprise-theme'
import { FullHeaderLayout } from '@thg-commerce/enterprise-core/src/Layouts'
import { NextConfig } from '@thg-commerce/enterprise-core/src/ConfigurationLoader/types'
import { PageType } from '@thg-commerce/enterprise-core/src/App/types'
import { EnterpriseRequest } from '@thg-commerce/enterprise-core/src/Server/types'
import { loadConfiguration } from '@thg-commerce/enterprise-core/src/ConfigurationLoader/ConfigurationLoader'
import { clientConfig as clientConfigFactory } from '@thg-commerce/enterprise-network/src/ApolloProvider/utils'
import { createApolloWithRequest } from '@thg-commerce/enterprise-core/src/Factory/apollo'
import {
  Stores as StoresResolver,
  StoresData,
} from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Content/Stores'
import { BreadcrumbsRenderer } from '@thg-commerce/enterprise-components'

import { Button } from '@thg-commerce/gravity-elements'
import { GridItem } from '@thg-commerce/gravity-system'
import { spacing } from '@thg-commerce/gravity-theme'
import { HorizontalAlignment } from '@thg-commerce/gravity-theme/alignments'

import { Stores as STORES_QUERY } from '../../../../src/graphql/Stores.graphql'
import { StoreSearchRenderer } from '../../../../src/renderer/StoreSearchRenderer'

import CustomError from '../../../_error'

interface StoresProps {
  storesData: {
    [key: string]: StoresData[]
  }
}
const regionsLengthData = (key, storesData) => {
  const initialData = storesData[key]
  return Object.values(initialData).length
}

const StyledParagraph = styled.p`
  ${Text('bodyText', 'default')};
`
const RegionButton = styled(Button)`
  ${Text('large1', 'alternate')}
  text-transform: capitalize;
  text-decoration: none;

  &:hover,
  &:focus,
  &:active {
    ${Text('large1', 'alternate')}
  }
`

const RegionGrid = styled.div`
  display: grid;
  row-gap: ${spacing(4)};
  column-gap: ${spacing(8)};
  grid-template-columns: repeat(1, 1fr);
  padding-bottom: ${spacing(10)};

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    grid-template-columns: repeat(3, 1fr);
  }
`

const Stores: EnterpriseNextPage<StoresProps> = ({ storesData }) => {
  const router = useRouter()
  const { enableStoreLocator, geocodeApiKeyStoreMap } = useSiteConfig()
  const { originUrl, brand, subsite } = useSiteDefinition()
  const [scriptReady, setScriptReady] = React.useState<boolean>(false)

  if (!enableStoreLocator || !storesData) {
    return <CustomError statusCode={404} />
  }
  const i18nStoreSearchTitle = i18n('storelocator.storesearch.indexpage.title')
  const i18nCountryName = i18n(`header.country.${subsite}.text`)
  const i18nText = {
    storesHeadTitle: i18n('stores.regions.head.title'),
    breadCrumbsHome: i18n('general.breadcrumbs.home.text'),
    breadCrumbsStores: i18n('breadcrumbs.all.stores'),
    storeSearchSubtitle: i18n('storelocator.storesearch.subtitle'),
    storeSearchInputLabel: i18n('storelocator.storesearch.inputlabel'),
    storeSearchButtonLabel: i18n('storelocator.storesearch.buttonlabel'),
    storesRegions: i18n('breadcrumbs.all.stores'),
    storeSearch: {
      title: vsprintf(i18nStoreSearchTitle, [
        brand.charAt(0).toUpperCase() + brand.slice(1),
        i18nCountryName,
      ]),
      subtitle: i18n('storelocator.storesearch.subtitle'),
      inputLabel: i18n('storelocator.storesearch.inputlabel'),
      buttonLabel: i18n('storelocator.storesearch.buttonlabel'),
      infoLabel: i18n('storelocator.storesearch.infolabel'),
      infoLinkLabel: i18n('storelocator.storesearch.infolinklabel'),
    },
  }

  const {
    query: { country },
  } = router
  const regionKeys = Object.keys(storesData) || []

  const storesCountry = typeof country === 'string' ? country : ''

  const breadcrumbItems = [
    {
      text: i18nText.breadCrumbsHome,
      url: Routes.HomePage,
    },
    {
      text: i18nText.breadCrumbsStores,
      url: Routes.Stores.replace(':country', storesCountry),
    },
  ]

  return (
    <React.Fragment>
      <Head>
        <title>{i18nText.storesHeadTitle}</title>
        <link
          rel="canonical"
          href={`${originUrl}${Routes.Stores.replace(
            ':country',
            storesCountry,
          )}`}
          key="link_canonical"
        />
      </Head>
      <BreadcrumbsRenderer
        schemaItems={breadcrumbItems.map((breadcrumb, index) => {
          return {
            '@type': 'ListItem',
            position: index + 1,
            name: breadcrumb.text,
            item: originUrl.concat(breadcrumb.url),
          }
        })}
        breadcrumbs={breadcrumbItems}
        capitalizeText
      />
      <PageWrapper compactMargin={true} enableMaxWidth={false}>
        <GridItem
          colSpan={12}
          rowStart={2}
          style={{
            paddingBottom: `${spacing(4)}`,
          }}
        >
          <StoreSearchRenderer
            i18nText={i18nText.storeSearch}
            alignment={HorizontalAlignment.CENTER}
            isSearchReady={scriptReady}
            onChangeRedirect={(value) =>
              router.push(`${Routes.StoreLocator}?location=${value}`)
            }
          />
        </GridItem>
        <GridItem
          colStart={2}
          colSpan={10}
          rowStart={3}
          style={{
            gridRowGap: `${spacing(2)}`,
            display: 'grid',
          }}
        >
          <RegionGrid>
            {regionKeys.map((item, index) => (
              <div
                style={{ display: 'flex', alignItems: 'center' }}
                key={index}
              >
                <RegionButton
                  sizing="regular"
                  emphasis="low"
                  renderedAs="a"
                  href={Routes.StoresRegion.replace(
                    ':country',
                    storesCountry,
                  ).replace(':region', item)}
                >
                  {regionKeys[index]}
                </RegionButton>
                <StyledParagraph>{`(${regionsLengthData(
                  item,
                  storesData,
                )})`}</StyledParagraph>
              </div>
            ))}
          </RegionGrid>
        </GridItem>
      </PageWrapper>
      <LoadScript
        id="google-map"
        googleMapsApiKey={geocodeApiKeyStoreMap || ''}
        libraries={['places']}
        loadingElement={<React.Fragment />}
        onLoad={() => setScriptReady(true)}
      />
    </React.Fragment>
  )
}
export default Stores

Stores.Layout = FullHeaderLayout

Stores.pageType = PageType.STORE_LOCATOR

export const getServerSideProps = withCacheConfiguration<
  {
    props: StoresProps
  },
  GetServerSidePropsContext
>(async (context) => {
  const nextConfig = getConfig() as NextConfig
  const request = context.req as EnterpriseRequest
  const config = await loadConfiguration(nextConfig, request)
  const apollo = createApolloWithRequest(request, config, true)

  const { publicRuntimeConfig } = config

  const allowOverrides = request.headers['x-enterprise-allow-overrides'] === '1'
  const allowSitePropertyOverrides =
    request.headers['x-enterprise-allow-site-property-overrides'] === '1'

  const clientConfig = clientConfigFactory(
    publicRuntimeConfig,
    allowOverrides,
    allowSitePropertyOverrides,
    request.config.enableVary,
  )

  apollo.setResolvers({
    Query: {
      stores: StoresResolver(apollo, clientConfig, publicRuntimeConfig),
    },
  })

  const { data } = await apollo.query({
    query: STORES_QUERY,
    fetchPolicy: 'no-cache',
  })

  return {
    props: {
      storesData: data.stores,
    },
  }
})
